import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type FocusEvent,
  type RefObject,
} from "react";
import {
  Alan,
  Anahtar,
  BosDurum,
  Buton,
  IkonArti,
  IkonButon,
  IkonCikis,
  IkonDefter,
  IkonDenetimKaydi,
  IkonKopyala,
  IkonOkul,
  IkonTik,
  Iskelet,
  Kart,
  OkulLogosu,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  TemaDugmesi,
  Uyari,
  type IkonBileseni,
} from "@kantin/ui";
import { yonetimAdresi } from "./yonetimAdresi.js";

const TABAN = "/api/v1/platform";

type Bolum = "okullar" | "paketler" | "audit";
type AbonelikDurumu = "deneme" | "aktif" | "askıda";

interface Okul {
  id: string;
  ad: string;
  kod: string;
  aktif: boolean;
  paket_id: string;
  paket_ad: string;
  abonelik_durum: AbonelikDurumu;
  bitis_tarihi: string | null;
}

interface Paket {
  id: string;
  kod: string;
  ad: string;
  aktif: boolean;
}

/** nokta.tur — @kantin/shared NoktaTuru ile aynı sıra. */
const NOKTA_TURU_ADI: Record<number, string> = {
  1: "Kantin",
  2: "Yemekhane",
  3: "Kırtasiye",
  4: "Otomat",
  5: "Depo",
};

/** personel.rol — @kantin/shared ROL_ADI ile aynı. */
const ROL_ADI: Record<number, string> = {
  1: "Kasiyer",
  2: "Nokta görevlisi",
  3: "Kantin müdürü",
  4: "Okul yönetimi",
  5: "Okul sistem yöneticisi",
  6: "Mali müşavir",
};

/** Okulun kendi ayarından ayrı; platform kilidi okul tarafından açılamaz. */
interface RolDurumu {
  rol: number;
  okul_acik: boolean;
  platform_kapali: boolean;
  aktif_personel: number;
}

interface Nokta {
  id: string;
  ad: string;
  tur: number;
  satis_yapar: boolean;
  aktif: boolean;
  terminal_sayisi: number;
}

interface AuditKaydi {
  id?: unknown;
  olusma_zamani?: unknown;
  eylem?: unknown;
  okul_id?: unknown;
  basarili?: unknown;
}

/** istek() 401 aldığında yayınlanır; Uygulama giriş ekranına döner. */
const OTURUM_DUSTU = "kantin:platform-oturum-dustu";

class ApiHatasi extends Error {
  constructor(
    mesaj: string,
    readonly http: number,
  ) {
    super(mesaj);
  }
}

async function istek<T>(yol: string, secenek: RequestInit = {}): Promise<T> {
  const yanit = await fetch(`${TABAN}${yol}`, {
    ...secenek,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(secenek.body ? { "Content-Type": "application/json" } : {}),
      ...secenek.headers,
    },
  });
  if (!yanit.ok) {
    const govde = (await yanit.json().catch(() => ({}))) as { mesaj?: string };
    const mesaj = govde.mesaj ?? `İstek başarısız (${yanit.status}).`;
    // Oturum düştü (süre doldu, iptal edildi, cookie yok): hangi ekranda
    // olursa olsun giriş ekranına dönülür. Aksi hâlde eski liste ekranda
    // kalır ve her işlem "oturum sona ermiş" hatasıyla biter.
    // Sunucu YETKISIZ'i 403 ile döner; platformda rol yoktur, 401/403'ün tek
    // anlamı "oturum yok". Diğer hatalar (409, 422) ekranda kalır.
    if (yanit.status === 401 || yanit.status === 403) {
      window.dispatchEvent(
        new CustomEvent<string>(OTURUM_DUSTU, { detail: mesaj }),
      );
    }
    throw new ApiHatasi(mesaj, yanit.status);
  }
  if (yanit.status === 204) return undefined as T;
  return yanit.json() as Promise<T>;
}

const BOLUMLER: {
  id: Bolum;
  baslik: string;
  kisa: string;
  aciklama: string;
  ikon: IkonBileseni;
}[] = [
  {
    id: "okullar",
    baslik: "Okullar",
    kisa: "Okullar",
    aciklama: "Okul hesapları, abonelikleri ve yönetim erişimleri",
    ikon: IkonOkul,
  },
  {
    id: "paketler",
    baslik: "Paketler",
    kisa: "Paketler",
    aciklama: "Platformda sunulan abonelik paketleri",
    ikon: IkonDefter,
  },
  {
    id: "audit",
    baslik: "Platform denetimi",
    kisa: "Denetim",
    aciklama: "Platform sahibi işlemlerinin değişmez kayıt izi",
    ikon: IkonDenetimKaydi,
  },
];

const ABONELIK_SECENEKLERI = [
  { deger: "deneme", metin: "Deneme" },
  { deger: "aktif", metin: "Aktif" },
  { deger: "askıda", metin: "Askıda" },
];

function hataMetni(hata: unknown): string {
  return hata instanceof Error ? hata.message : "Beklenmeyen bir hata oluştu.";
}

function tarihBicimle(deger: unknown): string {
  if (typeof deger !== "string" || !deger) return "—";
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return deger;
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(tarih);
}

function abonelikRozeti(durum: AbonelikDurumu) {
  const tur =
    durum === "aktif" ? "onay" : durum === "askıda" ? "hata" : "uyari";
  const metin =
    durum === "aktif" ? "Aktif" : durum === "askıda" ? "Askıda" : "Deneme";
  return <Rozet tur={tur}>{metin}</Rozet>;
}

export function Uygulama() {
  const [giris, setGiris] = useState<boolean | null>(null);
  const [bolum, setBolum] = useState<Bolum>("okullar");
  const [hata, setHata] = useState<string | null>(null);
  const [okullar, setOkullar] = useState<Okul[]>([]);
  const [paketler, setPaketler] = useState<Paket[]>([]);
  const [audit, setAudit] = useState<AuditKaydi[]>([]);
  const [auditYukleniyor, setAuditYukleniyor] = useState(false);
  const [terminalOkulu, setTerminalOkulu] = useState<Okul | null>(null);
  const [rolOkulu, setRolOkulu] = useState<Okul | null>(null);
  const okulDialog = useRef<HTMLDialogElement>(null);
  const terminalDialog = useRef<HTMLDialogElement>(null);
  const rolDialog = useRef<HTMLDialogElement>(null);

  const yenile = useCallback(async () => {
    try {
      const [okulYaniti, paketYaniti] = await Promise.all([
        istek<{ okullar: Okul[] }>("/okullar"),
        istek<{ paketler: Paket[] }>("/paketler"),
      ]);
      setOkullar(okulYaniti.okullar);
      setPaketler(paketYaniti.paketler);
      setGiris(true);
      setHata(null);
    } catch (e) {
      setGiris(false);
      setHata(hataMetni(e));
    }
  }, []);

  useEffect(() => {
    void yenile();
  }, [yenile]);

  // 401 → giriş ekranı. Yüklü veri temizlenir ki yeni giriş taze okusun.
  useEffect(() => {
    const dustu = (olay: Event) => {
      setGiris(false);
      setHata((olay as CustomEvent<string>).detail ?? "Oturum sona erdi.");
      setOkullar([]);
      setAudit([]);
      setTerminalOkulu(null);
      setRolOkulu(null);
    };
    window.addEventListener(OTURUM_DUSTU, dustu);
    return () => window.removeEventListener(OTURUM_DUSTU, dustu);
  }, []);

  useEffect(() => {
    if (!giris || bolum !== "audit") return;
    let gecerli = true;
    setAuditYukleniyor(true);
    setHata(null);
    void istek<{ kayitlar: AuditKaydi[] }>("/audit")
      .then((yanit) => {
        if (gecerli) setAudit(yanit.kayitlar);
      })
      .catch((e: unknown) => {
        if (gecerli) setHata(hataMetni(e));
      })
      .finally(() => {
        if (gecerli) setAuditYukleniyor(false);
      });
    return () => {
      gecerli = false;
    };
  }, [bolum, giris]);

  useEffect(() => {
    if (terminalOkulu && !terminalDialog.current?.open) {
      terminalDialog.current?.showModal();
    }
  }, [terminalOkulu]);

  useEffect(() => {
    if (rolOkulu && !rolDialog.current?.open) {
      rolDialog.current?.showModal();
    }
  }, [rolOkulu]);

  if (giris === null) return <Yukleniyor />;
  if (!giris) {
    return <Giris onGiris={() => void yenile()} ilkHata={hata} />;
  }

  const aktifBolum = BOLUMLER.find((oge) => oge.id === bolum)!;

  const cikis = async () => {
    try {
      await istek("/kimlik/cikis", { method: "POST" });
      setGiris(false);
      setHata(null);
    } catch (e) {
      setHata(hataMetni(e));
    }
  };

  const yonet = async (okul: Okul) => {
    try {
      setHata(null);
      const hedef = yonetimAdresi(
        window.location.href,
        import.meta.env.VITE_YONETIM_URL,
      );
      const yanit = await istek<{ token: string }>(
        `/okullar/${okul.id}/yonetim-oturumu`,
        { method: "POST" },
      );
      hedef.searchParams.set("platform_token", yanit.token);
      window.location.assign(hedef.href);
    } catch (e) {
      setHata(hataMetni(e));
    }
  };

  const abonelikGuncelle = async (
    okulId: string,
    yama: Record<string, string>,
  ) => {
    try {
      setHata(null);
      await istek(`/okullar/${okulId}`, {
        method: "PATCH",
        body: JSON.stringify(yama),
      });
      await yenile();
    } catch (e) {
      setHata(hataMetni(e));
    }
  };

  return (
    <div className="platform-kabuk">
      <UstCubuk bolum={bolum} bolumSec={setBolum} cikis={cikis} />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <MobilUstCubuk cikis={cikis} />

        <main className="platform-icerik" id="ana-icerik">
          <div>
            <div className="flex flex-col gap-6">
              <header className="platform-sayfa-basligi">
                <div className="min-w-0 max-w-icerik">
                  <h1 className="m-0 text-2xl">{aktifBolum.baslik}</h1>
                  <p className="m-0 mt-1 text-sm text-murekkep-2">
                    {aktifBolum.aciklama}
                  </p>
                </div>
                {bolum === "okullar" && (
                  <Buton
                    tur="birincil"
                    ikon={<IkonArti boyut={16} />}
                    onClick={() => okulDialog.current?.showModal()}
                  >
                    Okul oluştur
                  </Buton>
                )}
              </header>

              {hata && (
                <Uyari tur="hata" baslik="İşlem tamamlanamadı">
                  {hata}
                </Uyari>
              )}

              {bolum === "okullar" && (
                <Okullar
                  okullar={okullar}
                  paketler={paketler}
                  yonet={yonet}
                  abonelikGuncelle={abonelikGuncelle}
                  terminalKodu={setTerminalOkulu}
                  rolleriYonet={setRolOkulu}
                  okulOlustur={() => okulDialog.current?.showModal()}
                />
              )}
              {bolum === "paketler" && (
                <Paketler paketler={paketler} onYenile={yenile} />
              )}
              {bolum === "audit" && (
                <AuditTablosu kayitlar={audit} yukleniyor={auditYukleniyor} />
              )}
            </div>
          </div>
        </main>
      </div>

      <MobilAltSerit bolum={bolum} bolumSec={setBolum} />
      <OkulForm dialog={okulDialog} paketler={paketler} onOlustu={yenile} />
      <TerminalKoduDialog
        dialog={terminalDialog}
        okul={terminalOkulu}
        kapat={() => setTerminalOkulu(null)}
      />
      <RolKilidiDialog
        dialog={rolDialog}
        okul={rolOkulu}
        kapat={() => setRolOkulu(null)}
      />
    </div>
  );
}

function Yukleniyor() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-app p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <MarkaRozeti />
          <h1 className="mt-3 text-xl">Kantin Platform</h1>
          <p className="m-0 mt-1 text-sm text-murekkep-2">
            Yönetim alanı hazırlanıyor
          </p>
        </div>
        <Kart>
          <Iskelet satir={4} />
        </Kart>
      </div>
    </div>
  );
}

function MarkaRozeti({ kucuk = false }: { kucuk?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center bg-simit font-bolum text-dolgu-ustu ${
        kucuk ? "h-7 w-7 rounded-md text-sm" : "h-9 w-9 rounded-lg text-md"
      }`}
    >
      K
    </span>
  );
}

function UstCubuk({
  bolum,
  bolumSec,
  cikis,
}: {
  bolum: Bolum;
  bolumSec: (bolum: Bolum) => void;
  cikis: () => Promise<void>;
}) {
  return (
    <header className="platform-ust-cubuk">
      <a className="platform-atla" href="#ana-icerik">
        İçeriğe geç
      </a>
      <div className="platform-marka">
        <MarkaRozeti kucuk />
        <div className="min-w-0">
          <p className="platform-marka-adi">
            Kantin<span>Platform</span>
          </p>
        </div>
      </div>

      <nav aria-label="Platform bölümleri" className="platform-menu">
        <ul>
          {BOLUMLER.map((oge) => (
            <li key={oge.id}>
              <button
                type="button"
                aria-current={bolum === oge.id ? "page" : undefined}
                onClick={() => bolumSec(oge.id)}
                className="platform-menu-dugmesi"
              >
                <span
                  className={
                    bolum === oge.id
                      ? "text-murekkep"
                      : "text-murekkep-3 transition-colors group-hover:text-murekkep-2"
                  }
                >
                  <oge.ikon boyut={20} />
                </span>
                <span>{oge.baslik}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="platform-hesap">
        <div className="platform-hesap-etiketi">
          <p className="m-0 truncate text-sm font-satir text-murekkep">
            Platform sahibi
          </p>
          <p className="m-0 truncate text-xs text-murekkep-3">Tüm okullar</p>
        </div>
        <TemaDugmesi />
        <IkonButon etiket="Çıkış" onClick={() => void cikis()}>
          <IkonCikis boyut={16} />
        </IkonButon>
      </div>
    </header>
  );
}

function MobilUstCubuk({ cikis }: { cikis: () => Promise<void> }) {
  return (
    <header className="platform-mobil-ust guvenli-ust flex shrink-0 items-center gap-2 border-b border-cetvel bg-kenar px-4 md:hidden">
      <MarkaRozeti kucuk />
      <div className="min-w-0 flex-1">
        <p className="platform-marka-adi">
          Kantin<span>Platform</span>
        </p>
        <p className="m-0 truncate text-xs text-murekkep-3">Merkezi yönetim</p>
      </div>
      <TemaDugmesi />
      <IkonButon etiket="Çıkış" onClick={() => void cikis()}>
        <IkonCikis boyut={16} />
      </IkonButon>
    </header>
  );
}

function MobilAltSerit({
  bolum,
  bolumSec,
}: {
  bolum: Bolum;
  bolumSec: (bolum: Bolum) => void;
}) {
  return (
    <nav
      aria-label="Platform bölümleri"
      className="guvenli-alt fixed inset-x-0 bottom-0 z-20 border-t border-cetvel bg-kenar md:hidden"
    >
      <ul className="m-0 flex list-none p-0">
        {BOLUMLER.map((oge) => (
          <li key={oge.id} className="flex-1">
            <button
              type="button"
              aria-current={bolum === oge.id ? "page" : undefined}
              onClick={() => bolumSec(oge.id)}
              className={`flex min-h-hedef w-full flex-col items-center justify-center gap-0.5 py-2 text-2xs transition-colors duration-defter ${
                bolum === oge.id
                  ? "font-satir text-murekkep"
                  : "text-murekkep-3"
              }`}
            >
              <oge.ikon boyut={19} />
              <span>{oge.kisa}</span>
              <span
                aria-hidden="true"
                className={`block h-0.5 w-6 rounded-tam ${
                  bolum === oge.id ? "bg-simit" : "bg-transparent"
                }`}
              />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Okullar({
  okullar,
  paketler,
  yonet,
  abonelikGuncelle,
  terminalKodu,
  rolleriYonet,
  okulOlustur,
}: {
  okullar: Okul[];
  paketler: Paket[];
  yonet: (okul: Okul) => Promise<void>;
  abonelikGuncelle: (
    okulId: string,
    yama: Record<string, string>,
  ) => Promise<void>;
  terminalKodu: (okul: Okul) => void;
  rolleriYonet: (okul: Okul) => void;
  okulOlustur: () => void;
}) {
  if (okullar.length === 0) {
    return (
      <Kart>
        <BosDurum
          baslik="Henüz okul yok"
          aciklama="İlk okul hesabını oluşturarak platformu kullanmaya başlayın"
          eylem={
            <Buton tur="birincil" onClick={okulOlustur}>
              Okul oluştur
            </Buton>
          }
        />
      </Kart>
    );
  }

  const paketSecenekleri = paketler.map((paket) => ({
    deger: paket.id,
    metin: `${paket.ad}${paket.aktif ? "" : " (pasif)"}`,
  }));

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      {okullar.map((okul) => (
        <Kart
          key={okul.id}
          baslik={
            <div className="flex min-w-0 items-center gap-2.5">
              <OkulLogosu ad={okul.ad} boyut={40}
                bilgiAdresi={`${TABAN}/okullar/${okul.id}/marka`} />
              <div className="min-w-0">
                <h2 className="m-0 truncate text-lg">{okul.ad}</h2>
                <p className="rakam m-0 font-mono text-xs text-murekkep-3">
                  {okul.kod}
                </p>
              </div>
            </div>
          }
          ustBilgi={abonelikRozeti(okul.abonelik_durum)}
          altBilgi={
            okul.bitis_tarihi
              ? `Abonelik bitişi: ${tarihBicimle(okul.bitis_tarihi)}`
              : "Süresiz abonelik"
          }
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Secim
              etiket="Paket"
              value={okul.paket_id}
              secenekler={paketSecenekleri}
              onChange={(e) =>
                void abonelikGuncelle(okul.id, { paket_id: e.target.value })
              }
            />
            <Secim
              etiket="Abonelik durumu"
              value={okul.abonelik_durum}
              secenekler={ABONELIK_SECENEKLERI}
              onChange={(e) =>
                void abonelikGuncelle(okul.id, {
                  abonelik_durum: e.target.value,
                })
              }
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Buton tur="birincil" onClick={() => void yonet(okul)}>
              Okulu yönet
            </Buton>
            <Buton tur="ikincil" onClick={() => terminalKodu(okul)}>
              Terminal kodu üret
            </Buton>
            <Buton tur="ikincil" onClick={() => rolleriYonet(okul)}>
              Roller
            </Buton>
          </div>
        </Kart>
      ))}
    </div>
  );
}

function Paketler({
  paketler,
  onYenile,
}: {
  paketler: Paket[];
  onYenile: () => Promise<void>;
}) {
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const ekle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const veri = new FormData(form);
    setHata(null);
    setGonderiliyor(true);
    try {
      await istek("/paketler", {
        method: "POST",
        body: JSON.stringify({
          kod: veri.get("kod"),
          ad: veri.get("ad"),
          aktif: true,
        }),
      });
      form.reset();
      await onYenile();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  const aktiflikDegistir = async (paket: Paket) => {
    setHata(null);
    try {
      await istek(`/paketler/${paket.id}`, {
        method: "PATCH",
        body: JSON.stringify({ aktif: !paket.aktif }),
      });
      await onYenile();
    } catch (e) {
      setHata(hataMetni(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Kart baslik="Yeni paket">
        <form
          className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto]"
          onSubmit={(e) => void ekle(e)}
        >
          <Alan
            etiket="Paket kodu"
            name="kod"
            placeholder="TEMEL"
            autoComplete="off"
            required
          />
          <Alan
            etiket="Paket adı"
            name="ad"
            placeholder="Temel Paket"
            required
          />
          <Buton tur="birincil" type="submit" yukleniyor={gonderiliyor}>
            Ekle
          </Buton>
        </form>
      </Kart>

      {hata && (
        <Uyari tur="hata" baslik="Paket işlemi tamamlanamadı">
          {hata}
        </Uyari>
      )}

      {paketler.length === 0 ? (
        <Kart>
          <BosDurum
            baslik="Henüz paket yok"
            aciklama="Yukarıdaki formdan ilk abonelik paketini ekleyin"
          />
        </Kart>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {paketler.map((paket) => (
            <Kart
              key={paket.id}
              baslik={paket.ad}
              ustBilgi={
                <Rozet tur={paket.aktif ? "onay" : "notr"}>
                  {paket.aktif ? "Aktif" : "Pasif"}
                </Rozet>
              }
            >
              <p className="rakam m-0 mb-4 font-mono text-xs text-murekkep-3">
                {paket.kod}
              </p>
              <Buton
                tur={paket.aktif ? "tehlike" : "onay"}
                onClick={() => void aktiflikDegistir(paket)}
              >
                {paket.aktif ? "Pasife al" : "Etkinleştir"}
              </Buton>
            </Kart>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditTablosu({
  kayitlar,
  yukleniyor,
}: {
  kayitlar: AuditKaydi[];
  yukleniyor: boolean;
}) {
  if (yukleniyor) {
    return (
      <Kart>
        <Iskelet satir={6} />
      </Kart>
    );
  }
  if (kayitlar.length === 0) {
    return (
      <Kart>
        <BosDurum
          baslik="Denetim kaydı yok"
          aciklama="Platform sahibi işlemleri burada görünecek"
        />
      </Kart>
    );
  }
  return (
    <Tablo>
      <TabloBaslik>
        <th>Zaman</th>
        <th>Eylem</th>
        <th>Okul</th>
        <th>Sonuç</th>
      </TabloBaslik>
      <TabloGovde>
        {kayitlar.map((kayit, sira) => (
          <tr key={String(kayit.id ?? sira)}>
            <td className="whitespace-nowrap text-murekkep-2">
              {tarihBicimle(kayit.olusma_zamani)}
            </td>
            <td className="font-satir text-murekkep">
              {String(kayit.eylem ?? "—")}
            </td>
            <td className="rakam font-mono text-xs text-murekkep-2">
              {String(kayit.okul_id ?? "—")}
            </td>
            <td>
              <Rozet tur={kayit.basarili ? "onay" : "hata"}>
                {kayit.basarili ? "Başarılı" : "Başarısız"}
              </Rozet>
            </td>
          </tr>
        ))}
      </TabloGovde>
    </Tablo>
  );
}

function Giris({
  onGiris,
  ilkHata,
}: {
  onGiris: () => void;
  ilkHata: string | null;
}) {
  const [eposta, setEposta] = useState("");
  const [parola, setParola] = useState("");
  const [hata, setHata] = useState(ilkHata);
  const [degisim, setDegisim] = useState<string | null>(null);
  const [yeni, setYeni] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const gonder = async (e: FormEvent) => {
    e.preventDefault();
    setHata(null);
    setGonderiliyor(true);
    try {
      if (degisim) {
        await istek("/kimlik/parola-degistir", {
          method: "POST",
          body: JSON.stringify({ degisim_token: degisim, yeni_parola: yeni }),
        });
        setDegisim(null);
        setParola("");
        setYeni("");
        setHata("Parola değişti; yeni parolanızla giriş yapın.");
        return;
      }
      const yanit = await istek<{
        parola_degisim_zorunlu?: boolean;
        degisim_token?: string;
      }>("/kimlik/giris", {
        method: "POST",
        body: JSON.stringify({ eposta, parola }),
      });
      if (yanit.parola_degisim_zorunlu && yanit.degisim_token) {
        setDegisim(yanit.degisim_token);
        setParola("");
      } else {
        onGiris();
      }
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  const basari = hata?.startsWith("Parola değişti") ?? false;

  return (
    <div className="platform-giris">
      <aside className="platform-giris-hikaye">
        <div className="platform-marka">
          <MarkaRozeti />
          <span className="platform-marka-adi">KantinPlatform</span>
        </div>
        <div className="platform-giris-mesaji">
          <h2>
            Tüm okullarınız,
            <br />
            tek bir merkezde.
          </h2>
          <p>
            Okul hesaplarını, abonelikleri ve yönetim erişimlerini sade, hızlı
            bir arayüzden yönetin.
          </p>
        </div>
        <div className="platform-giris-ozellikler">
          <div>
            <strong>Okullar</strong>
            <span>Merkezi yönetim</span>
          </div>
          <div>
            <strong>Abonelikler</strong>
            <span>Tek yerden takip</span>
          </div>
          <div>
            <strong>Denetim</strong>
            <span>Her işlem kayıtlı</span>
          </div>
        </div>
      </aside>
      <div className="absolute right-4 top-4 z-10">
        <TemaDugmesi />
      </div>
      <main className="platform-giris-form-alani">
        <div className="platform-giris-form">
          <div className="mb-8">
            <h1 className="m-0 text-2xl">
              {degisim ? "Kalıcı parola belirleyin" : "Tekrar hoş geldiniz"}
            </h1>
            <p className="m-0 mt-1 text-sm text-murekkep-2">
              Okullarınızı yönetmeye devam etmek için platform hesabınıza giriş
              yapın.
            </p>
          </div>

          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void gonder(e)}
          >
            {hata && (
              <Uyari
                tur={basari ? "onay" : "hata"}
                baslik={basari ? "Parola değiştirildi" : "Giriş yapılamadı"}
              >
                {hata}
              </Uyari>
            )}

            {degisim ? (
              <>
                <Uyari tur="uyari" baslik="Parola değişimi zorunlu">
                  En az 8 karakterlik kalıcı parolanızı belirleyin.
                </Uyari>
                <Alan
                  etiket="Yeni parola"
                  type="password"
                  autoComplete="new-password"
                  value={yeni}
                  minLength={8}
                  required
                  onChange={(e) => setYeni(e.target.value)}
                />
              </>
            ) : (
              <>
                <Alan
                  etiket="E-posta"
                  type="email"
                  autoComplete="username"
                  value={eposta}
                  required
                  onChange={(e) => setEposta(e.target.value)}
                />
                <Alan
                  etiket="Parola"
                  type="password"
                  autoComplete="current-password"
                  value={parola}
                  required
                  onChange={(e) => setParola(e.target.value)}
                />
              </>
            )}

            <Buton
              type="submit"
              tur="birincil"
              tamGenislik
              yukleniyor={gonderiliyor}
            >
              {degisim ? "Parolayı değiştir" : "Giriş yap"}
            </Buton>
          </form>
          <p className="platform-giris-dipnot">
            Kantin Platform · Merkezi okul yönetimi
          </p>
        </div>
      </main>
    </div>
  );
}

function DialogBasligi({
  baslik,
  aciklama,
}: {
  baslik: string;
  aciklama?: string;
}) {
  return (
    <header className="border-b border-cetvel-silik px-5 py-4">
      <h2 className="m-0 text-xl">{baslik}</h2>
      {aciklama && (
        <p className="m-0 mt-1 text-sm text-murekkep-2">{aciklama}</p>
      )}
    </header>
  );
}

function OkulForm({
  dialog,
  paketler,
  onOlustu,
}: {
  dialog: RefObject<HTMLDialogElement>;
  paketler: Paket[];
  onOlustu: () => Promise<void>;
}) {
  const [sir, setSir] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);
  const kopyaZamanlayici = useRef<number | null>(null);

  // "Kopyalandı" göstergesi zamanlayıcıyla söner; bileşen sökülürse
  // zamanlayıcı arda kalmamalı (kurulum kodu dialoguyla aynı desen).
  useEffect(
    () => () => {
      if (kopyaZamanlayici.current !== null) {
        window.clearTimeout(kopyaZamanlayici.current);
      }
    },
    [],
  );

  const kopyala = async () => {
    if (!sir) return;
    try {
      await navigator.clipboard.writeText(sir);
      setKopyalandi(true);
      if (kopyaZamanlayici.current !== null) {
        window.clearTimeout(kopyaZamanlayici.current);
      }
      kopyaZamanlayici.current = window.setTimeout(
        () => setKopyalandi(false),
        2000,
      );
    } catch {
      setHata("Panoya kopyalanamadı; parolayı elle seçip kopyalayın.");
    }
  };

  const kodOner = async (e: FocusEvent<HTMLInputElement>) => {
    const kod = e.currentTarget.form?.elements.namedItem(
      "kod",
    ) as HTMLInputElement | null;
    if (!kod || kod.value || e.currentTarget.value.length < 3) return;
    try {
      const yanit = await istek<{ kod: string }>(
        `/okul-kodu-oner?ad=${encodeURIComponent(e.currentTarget.value)}`,
      );
      kod.value = yanit.kod;
    } catch (e) {
      setHata(hataMetni(e));
    }
  };

  const gonder = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const veri = new FormData(e.currentTarget);
    setHata(null);
    setGonderiliyor(true);
    try {
      const yanit = await istek<{ gecici_parola: string }>("/okullar", {
        method: "POST",
        body: JSON.stringify({
          ad: veri.get("ad"),
          kod: veri.get("kod"),
          paket_id: veri.get("paket"),
          abonelik_durum: veri.get("durum"),
          yonetici: {
            ad: veri.get("yad"),
            soyad: veri.get("ysoyad"),
            eposta: veri.get("eposta"),
          },
        }),
      });
      setSir(yanit.gecici_parola);
      await onOlustu();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  const kapat = () => {
    setSir(null);
    setHata(null);
    setKopyalandi(false);
    dialog.current?.close();
  };

  return (
    <dialog
      ref={dialog}
      onClose={() => {
        setSir(null);
        setHata(null);
        setKopyalandi(false);
      }}
    >
      <DialogBasligi
        baslik="Okul oluştur"
        aciklama="Okul hesabını, aboneliğini ve ilk yöneticisini tek adımda tanımlayın"
      />
      <form
        className="flex flex-col gap-4 p-5"
        onSubmit={(e) => void gonder(e)}
      >
        {hata && (
          <Uyari tur="hata" baslik="Okul oluşturulamadı">
            {hata}
          </Uyari>
        )}

        {sir ? (
          <>
            <Uyari tur="uyari" baslik="Geçici parolayı şimdi kaydedin">
              Bu parola yalnızca bir kez gösterilir.
            </Uyari>
            <div className="rounded-lg border border-cetvel bg-kagit-2 p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <code className="rakam select-all font-mono text-xl font-bolum text-murekkep">
                  {sir}
                </code>
                <IkonButon
                  etiket={kopyalandi ? "Kopyalandı" : "Parolayı kopyala"}
                  onClick={() => void kopyala()}
                  className={kopyalandi ? "text-onay" : ""}
                >
                  {kopyalandi ? <IkonTik /> : <IkonKopyala />}
                </IkonButon>
              </div>
              <p
                role="status"
                aria-live="polite"
                className="gizli-erisilebilir"
              >
                {kopyalandi ? "Geçici parola panoya kopyalandı." : ""}
              </p>
            </div>
            <div className="flex justify-end">
              <Buton tur="birincil" type="button" onClick={kapat}>
                Tamam
              </Buton>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Alan
                className="sm:col-span-2"
                etiket="Okul adı"
                name="ad"
                minLength={3}
                required
                onBlur={(e) => void kodOner(e)}
              />
              <Alan
                etiket="Değişmez okul kodu"
                name="kod"
                pattern="[A-Z0-9-]{3,20}"
                required
              />
              <Secim
                etiket="Paket"
                name="paket"
                secenekler={paketler
                  .filter((paket) => paket.aktif)
                  .map((paket) => ({ deger: paket.id, metin: paket.ad }))}
              />
              <Secim
                etiket="Abonelik durumu"
                name="durum"
                secenekler={ABONELIK_SECENEKLERI}
              />
            </div>

            <div className="border-t border-cetvel-silik pt-4">
              <h3 className="m-0 mb-3 text-lg">İlk okul sistem yöneticisi</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Alan
                  etiket="Ad"
                  name="yad"
                  autoComplete="given-name"
                  required
                />
                <Alan
                  etiket="Soyad"
                  name="ysoyad"
                  autoComplete="family-name"
                  required
                />
                <Alan
                  className="sm:col-span-2"
                  etiket="E-posta"
                  name="eposta"
                  type="email"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-cetvel-silik pt-4">
              <Buton tur="ikincil" type="button" onClick={kapat}>
                Vazgeç
              </Buton>
              <Buton tur="birincil" type="submit" yukleniyor={gonderiliyor}>
                Okul oluştur
              </Buton>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}

/**
 * Platform rol kilidi. Anahtar "rol AÇIK mı" sorusunu sorar; sunucuya
 * gönderilen ise KAPALI rollerin listesidir (0021 `platform_kapali_roller`).
 * Kapatmak yalnızca YENİ atamaları engeller — o rolde çalışan personelin
 * erişimi aynen sürer, bu yüzden aktif personel sayısı ayrıca gösterilir.
 */
function RolKilidiDialog({
  dialog,
  okul,
  kapat,
}: {
  dialog: RefObject<HTMLDialogElement>;
  okul: Okul | null;
  kapat: () => void;
}) {
  const [roller, setRoller] = useState<RolDurumu[] | null>(null);
  const [kapali, setKapali] = useState<number[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [kaydedildi, setKaydedildi] = useState(false);

  useEffect(() => {
    if (!okul) return;
    let iptal = false;
    setRoller(null);
    setKapali([]);
    setHata(null);
    setKaydedildi(false);
    void (async () => {
      try {
        const yanit = await istek<{ roller: RolDurumu[] }>(
          `/okullar/${okul.id}/roller`,
        );
        if (iptal) return;
        setRoller(yanit.roller);
        setKapali(
          yanit.roller.filter((r) => r.platform_kapali).map((r) => r.rol),
        );
      } catch (e) {
        if (!iptal) setHata(hataMetni(e));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [okul]);

  const sifirla = () => {
    setRoller(null);
    setKapali([]);
    setHata(null);
    setKaydedildi(false);
  };

  const temizleVeKapat = () => {
    sifirla();
    dialog.current?.close();
    kapat();
  };

  const degistir = (rol: number, acik: boolean) => {
    setKaydedildi(false);
    setKapali((onceki) =>
      acik ? onceki.filter((r) => r !== rol) : [...onceki, rol],
    );
  };

  const kaydet = async (e: FormEvent) => {
    e.preventDefault();
    if (!okul || roller === null) return;
    setHata(null);
    setGonderiliyor(true);
    try {
      const yanit = await istek<{ platform_kapali_roller: number[] }>(
        `/okullar/${okul.id}/roller`,
        {
          method: "PATCH",
          body: JSON.stringify({ platform_kapali_roller: kapali }),
        },
      );
      setKapali(yanit.platform_kapali_roller);
      setRoller((onceki) =>
        onceki === null
          ? onceki
          : onceki.map((r) => ({
              ...r,
              platform_kapali: yanit.platform_kapali_roller.includes(r.rol),
            })),
      );
      setKaydedildi(true);
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  const yukleniyor = okul !== null && roller === null && hata === null;
  const kapatilanCalisan = (roller ?? []).filter(
    (r) => kapali.includes(r.rol) && r.aktif_personel > 0,
  );

  return (
    <dialog
      ref={dialog}
      onCancel={(e) => {
        e.preventDefault();
        temizleVeKapat();
      }}
      onClose={() => {
        sifirla();
        kapat();
      }}
    >
      <DialogBasligi
        baslik="Kullanılabilir roller"
        aciklama={
          okul ? `${okul.ad} için rol atamalarını sınırlayın` : undefined
        }
      />
      <form
        className="flex flex-col gap-4 p-5"
        onSubmit={(e) => void kaydet(e)}
      >
        {hata && (
          <Uyari tur="hata" baslik="Roller kaydedilemedi">
            {hata}
          </Uyari>
        )}
        {kaydedildi && !hata && (
          <Uyari tur="bilgi" baslik="Kaydedildi">
            Yeni atamalar bu kısıtla yapılır; mevcut personelin erişimi
            değişmedi.
          </Uyari>
        )}
        {kapatilanCalisan.length > 0 && (
          <Uyari tur="uyari" baslik="Kapatılan rolde çalışan personel var">
            {kapatilanCalisan
              .map(
                (r) =>
                  `${ROL_ADI[r.rol] ?? `Rol ${r.rol}`}: ${r.aktif_personel} kişi`,
              )
              .join(" · ")}
            . Erişimleri etkilenmez; yalnızca yeni atama yapılamaz.
          </Uyari>
        )}
        {yukleniyor ? (
          <Iskelet satir={4} />
        ) : (
          <div className="flex flex-col gap-3">
            {(roller ?? []).map((r) => {
              const zorunlu = r.rol === 5;
              return (
                <Anahtar
                  key={r.rol}
                  acik={!kapali.includes(r.rol)}
                  devreDisi={zorunlu || gonderiliyor}
                  etiket={ROL_ADI[r.rol] ?? `Rol ${r.rol}`}
                  aciklama={
                    zorunlu
                      ? "Zorunlu rol — kapatılamaz."
                      : `${r.aktif_personel} aktif personel${
                          r.okul_acik ? "" : " · okul kendi ayarında kapatmış"
                        }`
                  }
                  degistir={(acik) => degistir(r.rol, acik)}
                />
              );
            })}
          </div>
        )}
        <p className="m-0 text-xs text-murekkep-3">
          Kapattığınız rolü okul kendi ayarlarından geri açamaz. Çalışan
          hesaplar etkilenmez; yalnızca yeni rol ataması engellenir.
        </p>
        <div className="flex flex-wrap justify-end gap-2 border-t border-cetvel-silik pt-4">
          <Buton tur="ikincil" type="button" onClick={temizleVeKapat}>
            Kapat
          </Buton>
          <Buton
            tur="birincil"
            type="submit"
            disabled={roller === null}
            yukleniyor={gonderiliyor}
          >
            Kaydet
          </Buton>
        </div>
      </form>
    </dialog>
  );
}

function TerminalKoduDialog({
  dialog,
  okul,
  kapat,
}: {
  dialog: RefObject<HTMLDialogElement>;
  okul: Okul | null;
  kapat: () => void;
}) {
  const [noktaId, setNoktaId] = useState("");
  const [noktalar, setNoktalar] = useState<Nokta[] | null>(null);
  const [noktaHatasi, setNoktaHatasi] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<{
    kurulum_kodu: string;
    son_kullanma_zamani: string;
  } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);
  const kopyaZamanlayici = useRef<number | null>(null);

  // "Kopyalandı" göstergesi bir zamanlayıcıyla söner; dialog kapanır veya
  // bileşen sökülürse zamanlayıcı ARDA KALMAMALI (sökülmüş bileşene setState).
  useEffect(
    () => () => {
      if (kopyaZamanlayici.current !== null) {
        window.clearTimeout(kopyaZamanlayici.current);
      }
    },
    [],
  );

  // Dialog kapanınca `okul` null'a döner; yeniden açılışta bu etki tekrar
  // koşar ve liste TAZE gelir. Okul panelinden yeni açılan bir nokta,
  // dialogu kapatıp açmak dışında bir şey gerektirmez.
  useEffect(() => {
    if (!okul) return;
    let iptal = false;
    setNoktalar(null);
    setNoktaHatasi(null);
    setNoktaId("");
    void (async () => {
      try {
        const yanit = await istek<{ noktalar: Nokta[] }>(
          `/okullar/${okul.id}/noktalar`,
        );
        if (!iptal) setNoktalar(yanit.noktalar);
      } catch (e) {
        if (iptal) return;
        setNoktalar([]);
        setNoktaHatasi(hataMetni(e));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [okul]);

  // KK-413 / AK-19: terminal yalnızca AKTİF ve SATIŞ YAPAN bir noktaya
  // bağlanabilir. Depo (tur 5) listede görünmez; sunucu tarafındaki
  // `platform.terminal_kodu_ekle` kontrolü asıl güvenlik sınırıdır.
  const uygunNoktalar = (noktalar ?? []).filter(
    (n) => n.aktif && n.satis_yapar,
  );
  const yukleniyor = okul !== null && noktalar === null;
  const noktaYok = noktalar !== null && uygunNoktalar.length === 0;

  const sifirla = () => {
    if (kopyaZamanlayici.current !== null) {
      window.clearTimeout(kopyaZamanlayici.current);
      kopyaZamanlayici.current = null;
    }
    setKopyalandi(false);
    setNoktaId("");
    setNoktalar(null);
    setNoktaHatasi(null);
    setSonuc(null);
    setHata(null);
  };

  const temizleVeKapat = () => {
    sifirla();
    dialog.current?.close();
    kapat();
  };

  const gonder = async (e: FormEvent) => {
    e.preventDefault();
    if (!okul || !noktaId) return;
    setHata(null);
    setGonderiliyor(true);
    try {
      const yanit = await istek<{
        kurulum_kodu: string;
        son_kullanma_zamani: string;
      }>(`/okullar/${okul.id}/terminal-kodlari`, {
        method: "POST",
        body: JSON.stringify({ nokta_id: noktaId }),
      });
      setSonuc(yanit);
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  const kopyala = async () => {
    if (!sonuc) return;
    try {
      await navigator.clipboard.writeText(sonuc.kurulum_kodu);
      setKopyalandi(true);
      if (kopyaZamanlayici.current !== null) {
        window.clearTimeout(kopyaZamanlayici.current);
      }
      kopyaZamanlayici.current = window.setTimeout(
        () => setKopyalandi(false),
        2000,
      );
    } catch {
      // Pano izni reddedilebilir (tarayıcı ayarı, güvensiz bağlam). Kod
      // ekranda duruyor; kullanıcıyı elle seçmeye yönlendiririz.
      setHata("Panoya kopyalanamadı; kodu elle seçip kopyalayın.");
    }
  };

  const secilen = uygunNoktalar.find((n) => n.id === noktaId) ?? null;
  const secenekler = [
    {
      deger: "",
      metin: yukleniyor ? "Yükleniyor…" : "Satış noktası seçin",
    },
    ...uygunNoktalar.map((n) => ({
      deger: n.id,
      metin: `${n.ad} · ${NOKTA_TURU_ADI[n.tur] ?? "Nokta"}${
        n.terminal_sayisi > 0 ? ` · ${n.terminal_sayisi} terminal` : ""
      }`,
    })),
  ];

  return (
    <dialog
      ref={dialog}
      onCancel={(e) => {
        e.preventDefault();
        temizleVeKapat();
      }}
      onClose={() => {
        sifirla();
        kapat();
      }}
    >
      <DialogBasligi
        baslik="Terminal kurulum kodu"
        aciklama={
          okul ? `${okul.ad} için tek kullanımlık kod üretin` : undefined
        }
      />
      <form
        className="flex flex-col gap-4 p-5"
        onSubmit={(e) => void gonder(e)}
      >
        <Uyari tur="bilgi" baslik="Yeni terminal kurulumu içindir">
          Bu kod yeni bir terminal oluşturur. Kasada “Terminal kimliğini onar”
          ekranı varsa “Okulu yönet” → Terminal sağlığı üzerinden mevcut
          terminal için “Token yenile” işlemini kullanın.
        </Uyari>
        {hata && (
          <Uyari tur="hata" baslik="Kod üretilemedi">
            {hata}
          </Uyari>
        )}
        {sonuc ? (
          <>
            <Uyari tur="uyari" baslik="Kodu şimdi kaydedin">
              Kurulum kodu yalnızca bir kez gösterilir.
            </Uyari>
            <div className="rounded-lg border border-cetvel bg-kagit-2 p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <code className="rakam select-all font-mono text-2xl font-bolum tracking-wider text-murekkep">
                  {sonuc.kurulum_kodu}
                </code>
                <IkonButon
                  etiket={kopyalandi ? "Kopyalandı" : "Kodu kopyala"}
                  onClick={() => void kopyala()}
                  className={kopyalandi ? "text-onay" : ""}
                >
                  {kopyalandi ? <IkonTik /> : <IkonKopyala />}
                </IkonButon>
              </div>
              {/* Buton etiketi değişiyor ama odak butonda kaldığı için ekran
                  okuyucu bunu kendiliğinden duyurmaz; durum ayrıca bildirilir. */}
              <p
                role="status"
                aria-live="polite"
                className="gizli-erisilebilir"
              >
                {kopyalandi ? "Kurulum kodu panoya kopyalandı." : ""}
              </p>
              <p className="m-0 mt-2 text-xs text-murekkep-3">
                Son kullanım: {tarihBicimle(sonuc.son_kullanma_zamani)}
              </p>
            </div>
            <div className="flex justify-end">
              <Buton tur="birincil" type="button" onClick={temizleVeKapat}>
                Tamam
              </Buton>
            </div>
          </>
        ) : (
          <>
            {noktaHatasi && (
              <Uyari tur="hata" baslik="Satış noktaları getirilemedi">
                {noktaHatasi}
              </Uyari>
            )}
            {noktaYok && !noktaHatasi && (
              <Uyari tur="uyari" baslik="Terminal bağlanabilecek nokta yok">
                Bu okulda aktif ve satış yapan bir nokta tanımlı değil. Depo
                noktalarına terminal bağlanamaz; okul yönetim panelinden önce
                bir satış noktası açılmalıdır.
              </Uyari>
            )}
            <Secim
              etiket="Satış noktası"
              value={noktaId}
              required
              disabled={yukleniyor || noktaYok}
              secenekler={secenekler}
              onChange={(e) => setNoktaId(e.target.value)}
            />
            <p className="m-0 -mt-2 text-xs text-murekkep-3">
              {secilen
                ? `Terminal "${secilen.ad}" noktasına kalıcı olarak bağlanır; satış, stok ve vardiya bu noktaya yazılır.`
                : "Terminalin bağlanacağı satış noktasını seçin."}
            </p>
            <div className="flex flex-wrap justify-end gap-2 border-t border-cetvel-silik pt-4">
              <Buton tur="ikincil" type="button" onClick={temizleVeKapat}>
                Vazgeç
              </Buton>
              <Buton
                tur="birincil"
                type="submit"
                disabled={!noktaId}
                yukleniyor={gonderiliyor}
              >
                Kod üret
              </Buton>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}
